import { toast, ToastOptions } from "react-toastify";
import { THEME_KEY } from "utils/helper/states";

type ToastType = "success" | "error" | "info" | "warning";

interface ToastConfig extends ToastOptions {
  position?:
    | "top-right"
    | "top-left"
    | "top-center"
    | "bottom-right"
    | "bottom-left"
    | "bottom-center";
  autoClose?: number;
  theme?: "light" | "dark";
}

const defaultConfig: ToastConfig = {
  position: "top-right",
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

const getTheme = (): "light" | "dark" => {
  const storedTheme = localStorage.getItem(THEME_KEY);
  if (storedTheme === "dark" || storedTheme === "light") {
    return storedTheme;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

export const toastify = (
  type: ToastType,
  message: string,
  customConfig?: Partial<ToastConfig>
) => {
  try {
    if (!message) {
      console.warn("Toast message is required");
      return;
    }

    const config: ToastConfig = {
      ...defaultConfig,
      theme: getTheme(),
      ...customConfig,
    };

    switch (type) {
      case "success":
        toast.success(message, config);
        break;
      case "error":
        toast.error(message, config);
        break;
      case "info":
        toast.info(message, config);
        break;
      case "warning":
        toast.warning(message, config);
        break;
      default:
        toast(message, config);
    }
  } catch (error) {
    console.error("Failed to show toast:", error);
  }
};

// Utility functions for common use cases
export const successToast = (message: string, config?: Partial<ToastConfig>) =>
  toastify("success", message, config);

export const errorToast = (message: string, config?: Partial<ToastConfig>) =>
  toastify("error", message, config);

export const infoToast = (message: string, config?: Partial<ToastConfig>) =>
  toastify("info", message, config);

export const warningToast = (message: string, config?: Partial<ToastConfig>) =>
  toastify("warning", message, config);
